import { useState } from "react";
import { MessageSquare, Navigation, Pencil, Trash2, X } from "lucide-react";
import { formatDateTime } from "@/utils/formatDate";
import type { ZEMapAnnotation } from "../types";

interface Props {
  annotations: ZEMapAnnotation[];
  onFlyTo: (id: string) => void;
  onEdit: (annotation: ZEMapAnnotation) => void;
  onDelete: (id: string) => void;
  className?: string;
}

export function ZEAnnotationsPanel({ annotations, onFlyTo, onEdit, onDelete, className }: Props) {
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem("ze_ann_panel_collapsed") === "1",
  );

  const toggle = (next: boolean) => {
    setCollapsed(next);
    localStorage.setItem("ze_ann_panel_collapsed", next ? "1" : "0");
  };

  if (collapsed) {
    return (
      <button
        type="button"
        onClick={() => toggle(false)}
        title="Adnotacje mapowe"
        aria-label="Pokaż adnotacje mapowe"
        className={`z-[1000] card bg-[var(--color-bg-surface)] shadow-sm p-1.5 text-amber-600 hover:text-amber-700 transition-colors ${className ?? ""}`}
      >
        <span className="relative block">
          <MessageSquare size={16} />
          {annotations.length > 0 && (
            <span className="absolute -top-1 -right-1 min-w-[14px] h-[14px] rounded-full bg-amber-500 text-white text-[9px] font-bold flex items-center justify-center px-[2px] leading-none">
              {annotations.length > 99 ? "99+" : annotations.length}
            </span>
          )}
        </span>
      </button>
    );
  }

  return (
    <div
      className={`z-[1000] card bg-[var(--color-bg-surface)] shadow-sm font-mono text-[11px] text-[var(--color-text-secondary)] w-56 ${className ?? ""}`}
    >
      {/* Header */}
      <div className="flex items-center gap-1.5 px-2.5 py-1.5 border-b border-[var(--color-border)]">
        <MessageSquare size={12} className="text-amber-600 shrink-0" />
        <span className="section-label flex-1">Adnotacje</span>
        <span className="text-[10px] text-[var(--color-text-muted)]">{annotations.length}</span>
        <button
          type="button"
          onClick={() => toggle(true)}
          title="Zwiń"
          className="ml-1 -mr-0.5 text-[var(--color-text-muted)] hover:text-[var(--color-primary)] transition-colors"
        >
          <X size={13} />
        </button>
      </div>

      {annotations.length === 0 ? (
        <p className="px-2.5 py-3 text-[11px] text-center text-[var(--color-text-muted)] normal-case">
          Brak adnotacji. Użyj trybu{" "}
          <strong className="text-amber-600">Adnotacja</strong> na mapie.
        </p>
      ) : (
        <ul className="max-h-72 overflow-y-auto divide-y divide-[var(--color-border)]">
          {annotations.map((ann) => (
            <li key={ann.id} className="group px-2.5 py-1.5">
              {/* Text preview */}
              <p className="text-[11px] leading-snug text-[var(--color-text-primary)] normal-case break-words line-clamp-2 mb-0.5">
                {ann.text}
              </p>
              {/* Author + date */}
              <div className="flex items-center justify-between gap-1">
                <div className="min-w-0">
                  {ann.author_name && (
                    <span className="block text-[10px] text-[var(--color-primary)] truncate font-semibold normal-case">
                      {ann.author_name}
                    </span>
                  )}
                  <span className="block text-[10px] text-[var(--color-text-muted)] normal-case">
                    {formatDateTime(ann.created_at)}
                  </span>
                </div>
                {/* Action icons — visible on hover */}
                <div className="shrink-0 flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    type="button"
                    title="Przejdź do pinu na mapie"
                    onClick={() => onFlyTo(ann.id)}
                    className="p-1 rounded hover:bg-blue-100 text-blue-500"
                  >
                    <Navigation size={11} />
                  </button>
                  <button
                    type="button"
                    title="Edytuj treść"
                    onClick={() => onEdit(ann)}
                    className="p-1 rounded hover:bg-amber-100 text-amber-600"
                  >
                    <Pencil size={11} />
                  </button>
                  <button
                    type="button"
                    title="Usuń adnotację"
                    onClick={() => {
                      if (window.confirm("Usunąć tę adnotację?")) onDelete(ann.id);
                    }}
                    className="p-1 rounded hover:bg-red-100 text-red-400"
                  >
                    <Trash2 size={11} />
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
